import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { ChatUI } from '../../components/chat/chatUI';
import { useChat } from '../../features/chat/useChat';

import {
  getAllInvites,
  getAllProfiles,
  getConnectStatus,
  getMyInvites,
  getOpportunityCard,
  getUserChatRooms,
  leaveChatRoom,
  updateDealStatus,
  updateMembersInGroup,
  verifyPayment,
} from '../../services/api';
import {
  AddMembersInChatRoom,
  ChatRequestDto,
  ChatRoom,
  ContactInvitesResponseDto,
  DealResponseDto,
  FileMetadataDto,
  ProfileId,
} from '../../types/types';
import { formatSportName } from '../../utils/utilMethods';
import {
  DealStatusEvent,
  encodeDealStatusMessage,
} from '../../utils/chat/dealStatusMessage';

export const Chat = () => {
  const [showViewDealModal, setShowViewDealModal] = useState(false);
  const [selectedDealForView, setSelectedDealForView] = useState<DealResponseDto | null>(null);
  const [viewDealHighlightSection, setViewDealHighlightSection] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [connectStatus, setConnectStatus] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'chats' | 'groups' | 'invites'>('chats');
  const [inviteSubTab, setInviteSubTab] = useState<'myInvites' | 'allRequests'>('allRequests');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | any>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [myInvites, setMyInvites] = useState<ContactInvitesResponseDto | null>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const [imageCarousel, setImageCarousel] = useState<{ images: FileMetadataDto[]; currentIndex: number } | null>(null);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [showGroupInfoModal, setShowGroupInfoModal] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const groupInfoRef = useRef<HTMLDivElement | any>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<ProfileId | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [dealProcessing, setDealProcessing] = useState<Record<string, boolean>>({});
  const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
  const loggedInUserName = loginResponse.username;

  const navigate = useNavigate();
  const MAX_CHAR_LIMIT = 10000;
  const [isLeaving, setIsLeaving] = useState(false);

  const { t, i18n } = useTranslation('chat');

  useEffect(() => {
    if (!loggedInUserName) return;
    const load = async () => {
      const status = await getConnectStatus();
      setConnectStatus(status);
    };
    load();
  }, [loggedInUserName]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const {
    contacts,
    selectedUser,
    selectedGroup,
    selectedChatType,
    setSelectedChatType,
    chatRooms,
    setChatRooms,
    messages,
    loading,
    message,
    sending,
    fetchingMessages,
    selectedFiles,
    selectedInvite,
    fileMetadataList,
    uploadingFiles,
    messagesEndRef,
    handleSelectContact,
    handleSelectGroup,
    handleSendMessage,
    handleSelectInvite,
    handleAcceptInvite,
    handleDeclineInvite,
    handleKeyPress,
    handleFileSelect,
    removeFile,
    clearFileSelection,
    getStatusColor,
    getLastMessageDisplay,
    getLastMessageDisplayForChatRoom,
    isMessageFromCurrentUser,
    setMessage,
    setSelectedGroup,
    fetchMessages,
    fetchGroupMessages,
    sendMessage,
    invites,
    setInvites,
  } = useChat();

  const sendDealStatusMessage = async (deal: DealResponseDto, event: DealStatusEvent) => {
    const payload: ChatRequestDto = {
      message: encodeDealStatusMessage(deal, event),
      fileMetadataList: [],
      type: 'CONTENT',
      chatType: selectedChatType === 'INVITE' ? 'INDIVIDUAL' : selectedChatType,
      receiverId: selectedUser ? selectedUser.profileDto.id : selectedGroup ? selectedGroup.id : undefined,
    };
    await sendMessage(payload);
  };

  const handleDealAction = async (deal: DealResponseDto, status: DealStatusEvent) => {
    if (dealProcessing[deal.id]) return;
    try {
      setDealProcessing((prev) => ({ ...prev, [deal.id]: true }));
      const updatedDeal = await updateDealStatus(deal.id, status);

      // The status is already saved at this point, so a failed message is only reported, never rolled back.
      try {
        await sendDealStatusMessage(updatedDeal, status);
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('dealStatusMessageFailed'));
      }

      if (selectedChatType === 'INDIVIDUAL' && selectedUser) {
        await fetchMessages(selectedUser.profileDto.id, 'INDIVIDUAL');
      } else if (selectedChatType === 'GROUP' && selectedGroup) {
        await fetchGroupMessages(selectedGroup.id);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setDealProcessing((prev) => ({ ...prev, [deal.id]: false }));
    }
  };

  const handleWithdrawDeal = (deal: DealResponseDto) => handleDealAction(deal, 'CANCELLED');

  useEffect(() => {
    if (activeTab === 'groups') {
      getUserChatRooms().then((data) => setChatRooms(data));
    }
    if (activeTab === 'invites') {
      setInvitesLoading(true);
      getAllInvites()
        .then((data) => {
          setInvites(data);
        })
        .catch((error) => {
          console.error('Error fetching invites:', error);
        })
        .finally(() => {
          setInvitesLoading(false);
        });
    }
  }, [activeTab, refreshFlag, setChatRooms, setInvites]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!(target instanceof Element && target.closest('[data-emoji-picker]'))) {
        setShowEmojiPicker(false);
      }
      if (groupInfoRef.current && !groupInfoRef.current.contains(target)) {
        setShowGroupInfoModal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLeaveGroup = async () => {
    if (!selectedGroup?.id) {
      toast.error(t('noGroupSelected'));
      return;
    }
    setIsLeaving(true);
    try {
      await leaveChatRoom(selectedGroup.id, loggedInUserName);
      setShowLeaveConfirm(false);
      setSelectedGroup(null);
      handleRefreshFlag();
      toast.success(t('leftGroupSuccess'));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('leaveGroupError'));
    } finally {
      setIsLeaving(false);
    }
  };

  const getCurrentUserRole = () => {
    const loginResponse = JSON.parse(localStorage.getItem('loginResponse') || '{}');
    return loginResponse.profileType || loginResponse.role;
  };

  const handleFetchMyInvites = () => {
    setInviteSubTab('myInvites');
    setInvitesLoading(true);
    getMyInvites()
      .then((data) => {
        setMyInvites(data);
      })
      .catch((error) => {
        setMyInvites(null);
      })
      .finally(() => {
        setInvitesLoading(false);
      });
  };

  const handleFetchAllRequests = () => {
    setInviteSubTab('allRequests');
    setInvitesLoading(true);
    getAllInvites()
      .then((data) => {
        setInvites(data);
      })
      .catch((error) => {})
      .finally(() => {
        setInvitesLoading(false);
      });
  };

  const handleRefreshFlag = () => {
    setRefreshFlag((prev) => !prev);
  };

  const handleViewProfile = (profileId?: string | number | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };

  const filteredChatRooms: ChatRoom[] = Array.isArray(chatRooms?.chatRoomResponseDtos)
    ? chatRooms!.chatRoomResponseDtos
    : [];

  return (
    <ChatUI
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      inviteSubTab={inviteSubTab}
      setInviteSubTab={setInviteSubTab}
      contacts={contacts}
      connectStatus={connectStatus}
      setConnectStatus={setConnectStatus}
      onViewProfile={handleViewProfile}
      loading={loading}
      chatRooms={chatRooms}
      filteredChatRooms={filteredChatRooms}
      invites={invites}
      myInvites={myInvites}
      invitesLoading={invitesLoading}
      selectedUser={selectedUser}
      selectedGroup={selectedGroup}
      selectedInvite={selectedInvite}
      handleSelectContact={handleSelectContact}
      handleSelectGroup={handleSelectGroup}
      handleSelectInvite={handleSelectInvite}
      setShowGroupModal={setShowGroupModal}
      setSelectedGroup={setSelectedGroup}
      handleFetchMyInvites={handleFetchMyInvites}
      handleFetchAllRequests={handleFetchAllRequests}
      getLastMessageDisplay={getLastMessageDisplay}
      getLastMessageDisplayForChatRoom={getLastMessageDisplayForChatRoom}
      formatText={formatSportName}
      t={t}
      selectedChatType={selectedChatType}
      fetchingMessages={fetchingMessages}
      showGroupInfoModal={showGroupInfoModal}
      setShowGroupInfoModal={setShowGroupInfoModal}
      groupInfoRef={groupInfoRef}
      isAddMemberOpen={isAddMemberOpen}
      setIsAddMemberOpen={setIsAddMemberOpen}
      handleAddPeople={async (memberIds: ProfileId[], groupId: number) => {
        const payload: AddMembersInChatRoom = {
          memberIds: memberIds,
          groupId: groupId,
        };
        try {
          await updateMembersInGroup(payload);
          toast.success('Members updated successfully');
          fetchGroupMessages(groupId);
        } catch (error: any) {
          toast.error(error?.response?.data?.message || 'Failed to add members');
        }
      }}
      getAllMembers={async (params: any) => {
        try {
          const response = await getAllProfiles(params.search);
          let transformedData = (response || []).map((profile: any, index: number) => ({
            id: profile.id || index,
            profileDto: profile,
          }));
          if (params.limit) {
            transformedData = transformedData.slice(0, params.limit);
          }
          return {
            data: transformedData,
          };
        } catch (error: any) {
          toast.error(error?.response?.data?.message || 'Failed to get all members');
        }
      }}
      handleRefreshFlag={handleRefreshFlag}
      showRemoveConfirm={showRemoveConfirm}
      setShowRemoveConfirm={setShowRemoveConfirm}
      memberToRemove={memberToRemove}
      setMemberToRemove={setMemberToRemove}
      fetchGroupMessages={fetchGroupMessages}
      loggedInUserName={loggedInUserName}
      showLeaveConfirm={showLeaveConfirm}
      setShowLeaveConfirm={setShowLeaveConfirm}
      handleLeaveGroup={handleLeaveGroup}
      isLeaving={isLeaving}
      setIsLeaving={setIsLeaving}
      messages={messages}
      isDragging={isDragging}
      handleAcceptInvite={handleAcceptInvite}
      handleDeclineInvite={handleDeclineInvite}
      chatAreaRef={chatAreaRef}
      messagesEndRef={messagesEndRef}
      isMessageFromCurrentUser={isMessageFromCurrentUser}
      selectedImage={selectedImage}
      setSelectedImage={setSelectedImage}
      selectedVideo={selectedVideo}
      setSelectedVideo={setSelectedVideo}
      imageCarousel={imageCarousel}
      setImageCarousel={setImageCarousel}
      handleWithdrawDeal={handleWithdrawDeal}
      dealProcessing={dealProcessing}
      getCurrentUserRole={getCurrentUserRole}
      setSelectedDealForView={setSelectedDealForView}
      setShowViewDealModal={setShowViewDealModal}
      viewDealHighlightSection={viewDealHighlightSection}
      setViewDealHighlightSection={setViewDealHighlightSection}
      setShowModal={setShowModal}
      message={message}
      setMessage={setMessage}
      selectedFiles={selectedFiles}
      sending={sending}
      uploadingFiles={uploadingFiles}
      handleSendMessage={handleSendMessage}
      handleKeyPress={handleKeyPress}
      handleFileSelect={handleFileSelect}
      removeFile={removeFile}
      clearFileSelection={clearFileSelection}
      fileInputRef={fileInputRef}
      showEmojiPicker={showEmojiPicker}
      setShowEmojiPicker={setShowEmojiPicker}
      inputAreaRef={inputAreaRef}
      MAX_CHAR_LIMIT={MAX_CHAR_LIMIT}
      showModal={showModal}
      showGroupModal={showGroupModal}
      showViewDealModal={showViewDealModal}
      selectedDealForView={selectedDealForView}
      fetchMessages={fetchMessages}
    />
  );
};
